"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Paperclip, ArrowUp, X, Loader2, Link as Url } from "lucide-react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, signInWithGoogle } from "../auth/firebase";
import { useGenerateDesignMutation } from "@/services/chatApi"; // ✅ add this import
import { useRunAgentMutation } from "@/services/agentApi";
import { setDesignData } from "../../store/designSlice";
import { useDispatch } from "react-redux";
import { setAllVersions, setCurrentVersion } from "@/store/versionSlice";
import { useGenerateScreenshotMutation } from "@/services/screenshotApi";
import { clearProjectInfo } from "@/store/projectInfoSlice";

/* Logo / small inline icons */
const GeometricIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="logo-gradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ac274f" />
        <stop offset="100%" stopColor="#eb638b" />
      </linearGradient>
    </defs>
    <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="url(#logo-gradient)" />
  </svg>
);

const StaticIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="inline-block">
    <rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 19h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const FullstackIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="inline-block">
    <rect x="3" y="4" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="6" y="14" width="12" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* small check icon inline */
const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function HeroSection() {
  const [inputValue, setInputValue] = useState("");
  const [user, setUser] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | "send">(null);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string>(""); // for image/text preview
  const dispatch = useDispatch();

  // App type state (Static by default)
  type AppType = "Static" | "Full_Stack";
  const [appType, setAppType] = useState<AppType>("Static");

  const router = useRouter();
  const [generateDesign] = useGenerateDesignMutation();
  const [generateScreenshot, { isLoading }] =
  useGenerateScreenshotMutation();
  const [runAgent] = useRunAgentMutation();
  // 🧠 Dynamic typing placeholder logic
  const fixedText = "Spin up a ";
  const ideas = [
    "slick landing page for your startup...",
    "portfolio website...",
    "todo app with drag & drop...",
    "fitness tracker dashboard...",
    "recipe finder app...",
  ];

  const [displayedText, setDisplayedText] = useState("");
  const [ideaIndex, setIdeaIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [cloneUrl, setCloneUrl] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const [generateVideo, setGenerateVideo] = useState(false);
  const [animateVideo, setAnimateVideo] = useState(false);


  useEffect(() => {
    const currentIdea = ideas[ideaIndex];
    let typingSpeed = isDeleting ? 20 : 60;

    const typingInterval = setTimeout(() => {
      if (!isDeleting && charIndex < currentIdea.length) {
        setDisplayedText(currentIdea.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (isDeleting && charIndex > 0) {
        setDisplayedText(currentIdea.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else if (!isDeleting && charIndex === currentIdea.length) {
        setTimeout(() => setIsDeleting(true), 1000);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setIdeaIndex((ideaIndex + 1) % ideas.length);
      }
    }, typingSpeed);

    return () => clearTimeout(typingInterval);
  }, [charIndex, isDeleting, ideaIndex]);

  // Persist Firebase user login
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && pendingAction === "send") {
      setShowAuthModal(false);
      setPendingAction(null);
      handleSendClick();
    }

  }, [user, pendingAction]);

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      setShowAuthModal(false);
    } catch (err) {
      console.error("Google login failed:", err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "text/plain",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, JPEG, PNG, PDF or TXT files are allowed.");
      return;
    }

    setAttachedFile(file);

    // IMAGE PREVIEW
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setFilePreview(reader.result as string);
      reader.readAsDataURL(file);
      return;
    }

    // TEXT PREVIEW
    if (file.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = () => setFilePreview(reader.result as string);
      reader.readAsText(file);
      return;
    }

    // PDF → no inline rendering, just filename
    if (file.type === "application/pdf") {
      setFilePreview("PDF_FILE"); // sentinel value
    }
  };

  function base64ToFile(base64: string, fileName: string, mimeType: string) {
    const byteString = atob(base64);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([uintArray], { type: mimeType });

    return new File([blob], fileName, { type: mimeType });
  }

  const handleCloneSubmit = async () => {
    if (!cloneUrl.trim() || !user) return;

    try {
      setIsCloning(true);

      const res = await generateScreenshot({
        url: cloneUrl.trim()
      }).unwrap();

      const mimeType =
        res.format === "jpeg" || res.format === "jpg"
          ? "image/jpeg"
          : "image/png";

      const fileName = `clone-screenshot.${res.format}`;

      const file = base64ToFile(res.base64Image, fileName, mimeType);

      // 👇 this is what you wanted
      setAttachedFile(file);
    } catch (e) {
      console.error("Clone failed", e);
    } finally {
      setIsCloning(false);
      setShowClone(false);
      setCloneUrl("");
    }
  };

const handleSendClick = async (e?: React.MouseEvent) => {
  if (e) e.preventDefault();
  if (!inputValue.trim() && !attachedFile) return;

  if (!user) {
    setPendingAction("send");
    setShowAuthModal(true);
    return;
  }

  try {
    setLoading(true);

    const agentResponse = await runAgent({
      user_id: user?.uid,
      user_query: inputValue.trim(),
      app_project_id: null,
      version_id: null,
      project_type: appType,
      additional_context: null,
      file_upload: attachedFile || null,
      generate_video: generateVideo,
      animate_video: animateVideo,
    }).unwrap();

    const tool = agentResponse?.tool_response;
    if (!tool) throw new Error("Invalid agent response");

    const appProjectId = tool.app_project_id;

    // ==============================
    //  CASE A: Agent needs user input
    // ==============================
    if (agentResponse.next_user_input === true) {
      dispatch(clearProjectInfo());

      // Store initial chat (Homepage → ChatSidebar)
      localStorage.setItem(
        "initialChat",
        JSON.stringify({
          userInput: inputValue,
          file: attachedFile ? attachedFile.name : null,
          filePreview,
          botResponse: agentResponse.message,
          app_project_id: appProjectId,
          app_type: appType,
          waitForUser: true, // <-- IMPORTANT FLAG
        })
      );

      // Save design (because pipeline didn't start yet)
      dispatch(setDesignData(tool));

      dispatch(setAllVersions(["version-1"]));
      dispatch(setCurrentVersion("version-1"));

      // Navigation WITHOUT starting pipeline
      router.push(`/project/${appProjectId}`);

      return; // stop – wait for ChatSidebar input
    }

    // ================================================
    //  CASE B: No user input needed → Start pipeline
    // ================================================
    dispatch(clearProjectInfo());
    dispatch(setDesignData(tool));
    dispatch(setAllVersions(["version-1"]));
    dispatch(setCurrentVersion("version-1"));

    localStorage.setItem(
      "initialChat",
      JSON.stringify({
        userInput: inputValue,
        file: attachedFile ? attachedFile.name : null,
        filePreview,
        botResponse: agentResponse.message,
        app_project_id: appProjectId,
        app_type: appType,
        waitForUser: false,
      })
    );

    router.push(`/project/${appProjectId}`);

  } catch (err) {
    console.error("Agent API failed:", err);
  } finally {
    setLoading(false);
  }
};



  /* UI helper: returns classes for option card */
  const optionBaseClasses =
    "group relative flex items-start gap-3 px-4 py-3 rounded-xl w-full transition-transform transform border";
  const optionHover =
    "hover:-translate-y-0.5 hover:shadow-lg focus-visible:-translate-y-0.5 focus-visible:shadow-lg";

  return (
    <div className="pt-8 md:pt-[4.75rem]">
      <div className="flex flex-col gap-5 w-full items-center justify-center px-3">
        {/* Logo & Header */}
        <div className="flex flex-col w-full items-center leading-tight">
          <div className="mb-3 md:mb-4">
            <Link href="/" className="flex items-center gap-2 shrink-0">
              <GeometricIcon size={64} />
              <span className="text-4xl font-medium text-white logo-text">
                Orignly
              </span>
            </Link>
          </div>
          <p className="hidden md:block text-xl text-white text-center">
            The AI Fullstack Engineer. Build prototypes, apps, and websites
          </p>
        </div>

        {/* Input Section */}
        <div className="relative max-w-full px-2 md:px-0 md:max-w-[630px] w-full">
          <div className="relative flex flex-col gap-1 py-2.5 rounded-lg bg-accent text-sm md:text-base">
            {attachedFile && (
              <div className="flex items-center gap-3 mx-1 px-3 py-2 w-[40%] text-sm border border-white/10 rounded-md mb-2 bg-black/20">
                
                {/* IMAGE */}
                {attachedFile.type.startsWith("image/") && (
                  <img
                    src={URL.createObjectURL(attachedFile)}
                    alt="preview"
                    className="w-10 h-10 object-cover rounded"
                  />
                )}

                {/* TEXT */}
                {attachedFile.type === "text/plain" && (
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded text-xs">
                    TXT
                  </div>
                )}

                {/* PDF */}
                {attachedFile.type === "application/pdf" && (
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500/20 text-red-400 rounded text-xs font-semibold">
                    PDF
                  </div>
                )}

                {/* FILE META */}
                <div className="flex flex-col overflow-hidden">
                  <span className="font-medium truncate max-w-[160px]">
                    {attachedFile.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {(attachedFile.size / 1024).toFixed(1)} KB
                  </span>
                </div>

                {/* REMOVE */}
                <button
                  onClick={() => {
                    setAttachedFile(null);
                    setFilePreview("");
                  }}
                  className="ml-auto text-xs text-red-400 hover:text-red-500"
                >
                  ✕
                </button>
              </div>
            )}

            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (inputValue.trim() || attachedFile) handleSendClick();
                }
              }}
              className="w-full outline-none resize-none min-h-[120px] max-h-[150px] px-2.5 text-base text-foreground bg-transparent placeholder:text-muted-foreground"
              placeholder={`${fixedText}${displayedText}`}
            />

            {/* <div className="flex items-center justify-end px-3 gap-3">
              <label className="cursor-pointer p-1 hover:bg-gray-700 rounded-md">
                <Paperclip className="h-4 w-4" />
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.txt"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>

              <button
                className={`flex items-center justify-center rounded-md p-1 transition-all ${
                  inputValue.trim() && !loading
                    ? "bg-foreground text-background hover:bg-foreground/90"
                    : "bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
                onClick={handleSendClick}
                disabled={!inputValue.trim() || loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div> */}
            {/* Row with small chips (left) and icons (right) */}
            <div className="flex items-center justify-between px-3">
              {/* left: tiny chips */}
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center bg-zinc-900 rounded-full border border-zinc-800 p-[2px] text-[11px]">
                  <button
                    type="button"
                    onClick={() => setAppType("Static")}
                    className={`flex items-center gap-1 px-3 py-[3px] rounded-full transition-all ${
                      appType === "Static"
                        ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-sm"
                        : "text-gray-300 hover:bg-zinc-800"
                    }`}
                  >
                    <StaticIcon size={12} />
                    <span>Static App</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAppType("Full_Stack")}
                    className={`flex items-center gap-1 px-3 py-[3px] rounded-full transition-all ${
                      appType === "Full_Stack"
                        ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-sm"
                        : "text-gray-300 hover:bg-zinc-800"
                    }`}
                  >
                    <FullstackIcon size={12} />
                    <span>Fullstack App</span>
                  </button>
                  
                </div>
                <div className="inline-flex items-center rounded-full border border-zinc-800 bg-zinc-900 text-[11px] cursor-pointer">
                    <button
                      type="button"
                      onClick={() => {
                        setGenerateVideo((value) => {
                          if (!value) setAnimateVideo(false);
                          return !value;
                        });
                      }}
                      aria-pressed={generateVideo}
                      className={`inline-flex items-center gap-2 cursor-pointer rounded-l-full px-3 py-[4px] transition-all ${
                        generateVideo
                          ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-sm"
                          : "text-gray-300 hover:bg-zinc-700"
                      }`}
                    >
                      <span>AI Video</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-current/80">
                        {generateVideo ? "On" : "Off"}
                      </span>
                    </button>
                    <div className="w-[1px] h-4 bg-zinc-800"></div>
                    <button
                      type="button"
                      onClick={() => {
                        setAnimateVideo((value) => {
                          if (!value) setGenerateVideo(false);
                          return !value;
                        });
                      }}
                      aria-pressed={animateVideo}
                      className={`inline-flex items-center gap-2 cursor-pointer rounded-r-full px-3 py-[4px] transition-all ${
                        animateVideo
                          ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-sm"
                          : "text-gray-300 hover:bg-zinc-700"
                      }`}
                    >
                      <span>3D Animate</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-current/80">
                        {animateVideo ? "On" : "Off"}
                      </span>
                    </button>
                </div>
              </div>
              

              {/* right: attach + send */}
              <div className="flex items-center gap-3">
                 <button
                    type="button"
                    onClick={() => setShowClone((v) => !v)}
                    className="p-1 rounded-md hover:bg-gray-700 transition-colors"
                    title="Clone an existing app"
                  >
                    {/* ⧉ */}
                    <Url className="h-4 w-4" />
                  </button>

                <label className="cursor-pointer p-1 hover:bg-gray-700 rounded-md">
                  <Paperclip className="h-4 w-4" />
                  <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.txt"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                </label>

                <button
                  className={`flex items-center justify-center rounded-md p-1 transition-all ${
                    (inputValue.trim() || attachedFile) && !loading ? "bg-foreground text-background hover:bg-foreground/90" : "bg-gray-700 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={handleSendClick}
                  disabled={(!inputValue.trim() && !attachedFile) || loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
            {showClone && (
              <div className=" absolute
                  left-0
                  top-full
                  w-full
                  z-50
                  bg-zinc-900
                  border border-white/10
                  rounded-lg
                  shadow-xl
                  p-2">
                <div className="flex items-center gap-2">
                  <input
                    value={cloneUrl}
                    onChange={(e) => setCloneUrl(e.target.value)}
                    placeholder="Provide URL of the app to clone"
                    className="
                      flex-1
                      bg-transparent
                      border border-white/10
                      rounded-md
                      px-2
                      py-2
                      text-xs
                      outline-none
                      text-foreground
                      placeholder:text-muted-foreground
                    "
                  />

                  <button
                    onClick={handleCloneSubmit}
                    disabled={!cloneUrl || isCloning}
                    className={`rounded-md px-3 py-2 text-xs font-medium transition-all ${
                      cloneUrl && !isCloning
                        ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white"
                        : "bg-gray-700 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {isCloning ? "Cloning..." : "Send"}
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* ================= NEW BLOCK OUTSIDE: Themed selector ================= */}
        {/* <div className="mt-4 max-w-full md:max-w-[1230px] w-full px-2 md:px-0">
          <div className="p-4 rounded-lg bg-accent border border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground">Application type</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAppType("Static")}
                aria-pressed={appType === "Static"}
                className={`${optionBaseClasses} ${optionHover} ${
                  appType === "Static"
                    ? "bg-gradient-to-r from-[#ac274f]/12 via-[#eb638b]/10 to-[#ff9fb1]/8 border-transparent ring-1 ring-white/10"
                    : "bg-transparent border-white/6"
                }`}
                title="Static Application — landing pages and marketing sites"
              >
                <div
                  className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
                    appType === "Static"
                      ? "bg-gradient-to-br from-[#ac274f] to-[#eb638b] text-white shadow-[0_6px_18px_rgba(172,39,79,0.12)]"
                      : "bg-white/6 text-foreground/90"
                  }`}
                >
                  <StaticIcon />
                </div>

                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-foreground">Static Application</div>
                    {appType === "Static" && (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/8 text-white">
                        <CheckIcon />
                        Selected
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Landing pages, marketing sites, and static content. Fast, deployable, and SEO-friendly.
                  </div>
                </div>

                <div className="ml-3 self-center text-sm text-muted-foreground group-hover:text-foreground/90">
                </div>
              </button>

              <button
                type="button"
                onClick={() => setAppType("Full_Stack")}
                aria-pressed={appType === "Full_Stack"}
                className={`${optionBaseClasses} ${optionHover} ${
                  appType === "Full_Stack"
                    ? "bg-gradient-to-r from-[#ac274f]/12 via-[#eb638b]/10 to-[#ff9fb1]/8 border-transparent ring-1 ring-white/10"
                    : "bg-transparent border-white/6"
                }`}
                title="Fullstack Application — APIs, DB and auth"
              >
                <div
                  className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
                    appType === "Full_Stack"
                      ? "bg-gradient-to-br from-[#ac274f] to-[#eb638b] text-white shadow-[0_6px_18px_rgba(172,39,79,0.12)]"
                      : "bg-white/6 text-foreground/90"
                  }`}
                >
                  <FullstackIcon />
                </div>

                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-foreground">Fullstack Application</div>
                    {appType === "Full_Stack" && (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/8 text-white">
                        <CheckIcon />
                        Selected
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    APIs, database integration, authentication, and server-side logic.
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div> */}

        <div className="w-full mb-12 md:mb-10"></div>
      </div>

      {/* Login Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[#1a1a1a] rounded-md p-8 w-96 flex flex-col items-center gap-6 relative">
            <button
              className="absolute top-3 right-3 text-white"
              onClick={() => setShowAuthModal(false)}
            >
              <X />
            </button>

            <div className="flex items-center justify-center gap-2">
              <GeometricIcon size={64} />
            </div>

            <h2 className="text-white text-lg font-semibold text-center">
              Sign in to Orignly
            </h2>
            <p className="text-gray-400 text-sm text-center">
              Welcome back! Please sign in to continue.
            </p>

            <button
              className="bg-white text-black rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:bg-gray-200 w-full"
              onClick={handleGoogleLogin}
            >
              <img src="/google-logo.png" alt="Google" className="w-5 h-5" />
              Continue with Google
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


